<script lang="ts" setup>
import LineMenu01 from '@docue/docue-ui-v2/icons/LineMenu01.vue'

import type * as API from '~/types/api'
import type { SignInvitation } from '~/types/sign'

const { t } = useI18n()
const { isNewArchiveEnabled } = useAuth()

const route = useRoute()
const isHomePage = computed(() => route.name === '2-home')

const isSearchVisible = computed(() => isNewArchiveEnabled.value && !isHomePage.value)

const { data: pendingSignInvitations, pending } = useAsyncData(
  () => useApi().getRequest<API.v1.Response<SignInvitation[]>>(
    '/me/sign-invitations?signatureState=waiting',
  )().then(r => r.data),
)

const signInvitationCount = computed(() => pendingSignInvitations.value?.length ?? 0)
</script>

<template>
  <header class="flex h-14 w-full shrink-0 border-b border-b-gray-200">
    <div
      class="grid grow items-center gap-4 px-4"
      :class="isSearchVisible ? 'grid-cols-[minmax(auto,min(15rem,100%)),1fr,auto]' : 'grid-cols-[minmax(auto,min(15rem,100%)),auto] justify-between'"
    >
      <AppUserMenu />

      <div
        v-if="isSearchVisible"
        class="ml-auto mr-0 flex items-center justify-center md:mx-auto md:w-full md:px-10"
      >
        <AppCommandPalette />
      </div>

      <div class="flex items-center gap-4">
        <UiPopover>
          <UiTooltip>
            <UiTooltipTrigger as-child>
              <UiPopoverTrigger as-child>
                <UiButton
                  variant="ghost"
                  aspect-square
                  class="relative focus-visible:outline-none"
                >
                  <UiIcon
                    name="docue:bell-01"
                    size="1.25rem"
                  />
                  <span
                    v-if="signInvitationCount > 0"
                    class="
                      absolute
                      -right-1.5
                      top-0
                      flex
                      size-4
                      items-center
                      justify-center
                      rounded-full
                      border-[1.5px]
                      border-white
                      bg-red-500
                      text-[8px]
                      text-white
                      "
                  >
                    {{ signInvitationCount }}
                  </span>
                </UiButton>
              </UiPopoverTrigger>
            </UiTooltipTrigger>
            <UiPopoverContent class="min-w-max px-3.5 py-4 pl-6">
              <DTLoaderDots v-if="pending" />
              <span
                v-else-if="signInvitationCount > 0"
                class="grid grid-cols-[min-content,auto] gap-3"
              >
                <i
                  class="inline-block size-1.5 self-center rounded-full bg-red-500"
                />
                <NuxtLink
                  to="/sign/invitations"
                  class="text-sm"
                >
                  {{
                    $t('header.notifications.pending-signatures', {
                      n: signInvitationCount,
                      count: signInvitationCount,
                    })
                  }}
                </NuxtLink>
              </span>
              <span
                v-else
                class="-ml-3 text-sm text-gray-600"
              >
                {{ $t('header.notifications.empty') }}
              </span>
            </UiPopoverContent>
            <UiTooltipContent>
              {{ t('header.notifications.tooltip') }}
            </UiTooltipContent>
          </UiTooltip>
        </UiPopover>

        <AppLocaleSelect icon-only />

        <DTPopover
          class="md:hidden"
          placement="bottom-end"
        >
          <template #button>
            <DTPopoverButton class="rounded-full">
              <DTIconButton
                as="div"
                variant="plain"
                dense
              >
                <LineMenu01 class="size-5 text-gray-600" />
              </DTIconButton>
            </DTPopoverButton>
          </template>
          <div class="p-1.5">
            <AppNavigation class="pt-2.5" />
          </div>
        </DTPopover>
      </div>
    </div>
  </header>
</template>
